import React, { createContext, useContext, useEffect, useMemo } from "react"
import { setUser } from "@sentry/react"
import {
	GetIdentityQuery,
	GetIdentityQueryVariables,
} from "generated/graphql-operations"
import {
	GetIdentityDocument,
	useUpdateProfileMutation,
} from "generated/graphql"
import { useApolloClient } from "@apollo/client"

import { RoleSlugEnum } from "../generated/graphql-types"
import i18n from "../i18n"

type UpdateProfileMutation = ReturnType<typeof useUpdateProfileMutation>

interface UserContextInterface {
	user: GetIdentityQuery["identity"]
	isAdministrative: boolean
	updateProfileMutation: UpdateProfileMutation[0]
	updateProfileResult: UpdateProfileMutation[1]
}
const UserContext = createContext<UserContextInterface | null>(null)

export const useUserContext = () => {
	const ctx = useContext(UserContext)

	if (ctx === null) {
		throw new Error("No UserContextProvider available.")
	}

	return ctx
}

const pref: {
	current: Promise<void> | undefined
} = {
	current: undefined,
}
const useUser = () => {
	const client = useApolloClient()
	const data = client.cache.readQuery<
		GetIdentityQuery,
		GetIdentityQueryVariables
	>({
		query: GetIdentityDocument,
	})

	useEffect(() => {
		pref.current = undefined

		if (process.env.NODE_ENV !== "production") {
			return
		}

		if (!data) {
			setUser(null)
			return
		}

		setUser({
			id: data.identity.id,
		})
	}, [data])

	if (!data) {
		if (pref.current) {
			throw pref.current
		}

		throw (pref.current = new Promise(async (res) => {
			try {
				await client.query<GetIdentityQuery, GetIdentityQueryVariables>(
					{
						query: GetIdentityDocument,
					}
				)
			} catch (e) {
				pref.current = undefined
				throw e
			}

			res()
		}))
	}

	return data.identity
}

export const UserContextProvider: React.FC = ({ children }) => {
	const user = useUser()

	useEffect(() => {
		;(async () => {
			await i18n.changeLanguage(user.language ?? undefined)
		})()
	}, [user])

	const isAdministrative = useMemo(() => {
		return (
			user.role.slug === RoleSlugEnum.Administrator ||
			user.role.slug === RoleSlugEnum.Production
		)
	}, [user])

	const [updateProfileMutation, updateProfileResult] =
		useUpdateProfileMutation()

	const ctx = {
		user,
		isAdministrative,
		updateProfileMutation,
		updateProfileResult,
	}

	return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>
}
